import { Link } from "react-router-dom";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import {
  LEGAL_ENTITY,
  LEGAL_DBA,
  SECURITY_PAGE_UPDATED,
  SUPPORT_EMAIL,
} from "@/lib/publicSite";

const SECURITY_TOC = [
  { id: "summary", label: "Summary" },
  { id: "architecture", label: "Architecture" },
  { id: "data", label: "Data we store" },
  { id: "access", label: "Access and operations" },
  { id: "report", label: "Report a vulnerability" },
  { id: "more", label: "More detail" },
] as const;

export function SecurityPage() {
  return (
    <PublicLayout title="ATW Hub Security">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Security"
          title="ATW Hub security"
          lede={
            <>
              How {LEGAL_ENTITY} (dba {LEGAL_DBA}) protects Hub and how to report a problem.
            </>
          }
          updated={SECURITY_PAGE_UPDATED}
        />

        <PublicDocLayout toc={[...SECURITY_TOC]}>
          <section id="summary" className="public-summary-card public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="summary" />
              Summary
            </h2>
            <ul className="public-list">
              <li>Hub uses HTTPS (TLS 1.2+) for all browser and API traffic.</li>
              <li>
                OAuth tokens for connected services are stored only on the Hub API host, encrypted at rest
                (AES-256-GCM; key material stays on the server), and are never sent to your browser.
              </li>
              <li>
                Chat and meeting message text loads live when you open a conversation; Hub does not write
                message text to disk or application logs.
              </li>
              <li>
                Slack and Zoom webhooks are verified with signing secrets before Hub acts on them.
              </li>
              <li>
                Sign-in goes through auth.atw.net ({LEGAL_DBA}'s central sign-in). Hub sessions use
                server-side validation.
              </li>
            </ul>
          </section>

          <section id="architecture" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="architecture" />
              Architecture
            </h2>
            <p>
              The Hub web app is hosted on Cloudflare Pages (hub.atw.net). The Hub API runs on AWS
              Lightsail in us-east-1 (Virginia). DNS and TLS termination for the public sites use
              Cloudflare. Connected services (Slack, Microsoft Graph, Zoom, Google, Zoho, GIPHY) and
              Anthropic, for Hub AI, are reached from the API over HTTPS.
            </p>
          </section>

          <section id="data" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="data" />
              Data we store
            </h2>
            <p>On the API host, Hub stores:</p>
            <ul className="public-list">
              <li>encrypted OAuth tokens and connection metadata for services you connect;</li>
              <li>read markers and saved Slack message links;</li>
              <li>Hub AI Slack tokens (separate from Chat) when you connect Hub AI;</li>
              <li>your tasks, and Hub AI conversations you keep in History (not ones where Hub AI read Slack);</li>
              <li>short-lived server logs (no message text) for operations and security.</li>
            </ul>
            <p>
              Your browser may cache recent mail in IndexedDB so Mail opens quickly. That cache is not
              cleared when you sign out of Hub; clear site data for hub.atw.net to remove it. See the{" "}
              <Link to="/privacy">Privacy Policy</Link> for retention and deletion.
            </p>
          </section>

          <section id="access" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="access" />
              Access and operations
            </h2>
            <p>
              Production API access is limited to {LEGAL_DBA} staff who operate Hub. Secrets (OAuth client
              secrets, signing secrets, encryption keys, and AI API keys) live only in the API host
              environment, not in the web app or public repositories.
            </p>
          </section>

          <section id="report" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="report" />
              Report a vulnerability
            </h2>
            <p>
              Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with “Security” in the subject.
              Include steps to reproduce and any proof-of-concept. We aim to acknowledge reports within 2
              business days. Please do not publicly disclose issues until we have had a reasonable time to
              fix them.
            </p>
            <p>
              You can also use our{" "}
              <a href="https://hub.atw.net/.well-known/security.txt">security.txt</a> file (RFC 9116).
            </p>
          </section>

          <section id="more" className="public-doc-section">
            <h2 className="public-section-title">
              <PublicSectionAnchor sectionId="more" />
              More detail
            </h2>
            <p>
              For marketplace or enterprise security questionnaires, email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will provide our detailed
              security overview.
            </p>
            <p>
              Questions about support hours and response times are on the <Link to="/support">Support</Link>{" "}
              page.
            </p>
          </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
